import { Tinode } from "tinode-sdk";
import {
  parseHostForSDK,
  shouldUseSecureConnection,
} from "../utils/tinodeHostParser";
import {
  parseTinodeError,
  isNonFatalTinodeError,
} from "../utils/tinodeErrorHelper";

const HOST_RAW = process.env.REACT_APP_TINODE_HOST;
if (!HOST_RAW) {
  throw new Error(
    "REACT_APP_TINODE_HOST is required. Please set it in your .env file",
  );
}
const HOST = parseHostForSDK(HOST_RAW);
const SECURE = shouldUseSecureConnection(HOST_RAW);
const API_KEY =
  process.env.REACT_APP_TINODE_API_KEY || "AQEAAAABAAD_rAp4DJh05a1HAwFT3A6K";

let tinodeInstance = null;
let isCreatingInstance = false;
let instancePromise = null;

let isConnecting = false;
let connectPromise = null;

const attemptRelogin = async (tinode) => {
  try {
    const {
      getStoredAuthToken,
      generateTinodeSecret,
      storeAuthToken,
      clearAuthToken,
    } = await import("./tinodeApi");

    const storedToken = getStoredAuthToken();
    if (storedToken?.token) {
      try {
        await tinode.loginToken(storedToken.token);
        const newToken = tinode.getAuthToken();
        if (newToken?.token) storeAuthToken(newToken);
        return true;
      } catch {
        clearAuthToken();
      }
    }

    const password = sessionStorage.getItem("tinode_password");
    const email = localStorage.getItem("email");
    if (password && email) {
      const secret = generateTinodeSecret(email, password);
      await tinode.login("basic", secret, null);
      const newToken = tinode.getAuthToken();
      if (newToken?.token) storeAuthToken(newToken);
      return true;
    }
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[Tinode] Re-login failed:", parseTinodeError(err));
    }
  }
  return false;
};

export const attemptTinodeRelogin = async (tinode) => attemptRelogin(tinode);

export const getTinodeInstance = async () => {
  if (tinodeInstance) {
    return tinodeInstance;
  }

  if (isCreatingInstance && instancePromise) {
    return instancePromise;
  }

  isCreatingInstance = true;
  instancePromise = new Promise((resolve, reject) => {
    try {
      const finalHost = parseHostForSDK(HOST);

      tinodeInstance = new Tinode({
        appName: "OLIN-PSEF",
        host: finalHost,
        apiKey: API_KEY,
        transport: "websocket",
        secure: SECURE || false,
        persist: false,
      });

      tinodeInstance.setHumanLanguage("en-US");

      if (process.env.NODE_ENV === "development") {
        tinodeInstance.enableLogging(true, true);
      }

      tinodeInstance.onDisconnect = (unexpected, code, reason) => {
        isConnecting = false;
        connectPromise = null;

        if (
          code === 504 ||
          reason?.includes("timeout") ||
          reason?.includes("Promise expired")
        ) {
          return;
        }

        if (unexpected && tinodeInstance && !tinodeInstance.isConnected()) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[Tinode] Unexpected disconnect:", { code, reason });
          }

          setTimeout(async () => {
            try {
              if (
                tinodeInstance &&
                !tinodeInstance.isConnected() &&
                !isConnecting
              ) {
                await connectTinodeInstance(tinodeInstance);
                if (!tinodeInstance.isAuthenticated()) {
                  await attemptRelogin(tinodeInstance);
                }
              }
            } catch (err) {
              if (process.env.NODE_ENV === "development") {
                console.warn(
                  "[Tinode] Auto-reconnect failed:",
                  parseTinodeError(err),
                );
              }
            }
          }, 2000);
        }
      };

      tinodeInstance.onConnect = (isConnected, code, reason) => {
        if (isConnected && tinodeInstance.isAuthenticated()) {
          if (process.env.NODE_ENV === "development") {
            console.log("[Tinode] Connected and authenticated, triggering refresh...");
          }
          
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("tinode-reconnected", {
              detail: { tinode: tinodeInstance }
            }));
          }
        }
      };

      if (typeof window !== "undefined") {
        const handleOnline = async () => {
          if (process.env.NODE_ENV === "development") {
            console.log("[Tinode] Network online, checking connection...");
          }
          
          if (tinodeInstance && !tinodeInstance.isConnected()) {
            try {
              await connectTinodeInstance(tinodeInstance);
              if (!tinodeInstance.isAuthenticated()) {
                await attemptRelogin(tinodeInstance);
              }
            } catch (err) {
              if (process.env.NODE_ENV === "development") {
                console.warn("[Tinode] Auto-reconnect on online failed:", parseTinodeError(err));
              }
            }
          }
        };

        window.addEventListener("online", handleOnline);
        
        // Store handler untuk cleanup nanti (jika diperlukan)
        tinodeInstance._onlineHandler = handleOnline;
      }

      tinodeInstance.onError = (err) => {
        if (isNonFatalTinodeError(err)) return;
        if (err instanceof Event || err?.isTrusted !== undefined) return;
        if (err?.type === "error" || err?.type === "close") return;

        if (process.env.NODE_ENV === "development") {
          console.warn("[Tinode] onError:", parseTinodeError(err));
        }
      };

      resolve(tinodeInstance);
      isCreatingInstance = false;
      instancePromise = null;
    } catch (error) {
      console.error("[Tinode] Failed to create instance:", error);
      isCreatingInstance = false;
      instancePromise = null;
      tinodeInstance = null;
      reject(error);
    }
  });

  return instancePromise;
};

export const connectTinodeInstance = async (tinode, timeoutMs = 12000) => {
  if (tinode.isConnected()) {
    return tinode;
  }

  if (isConnecting && connectPromise) {
    return connectPromise;
  }

  isConnecting = true;
  connectPromise = new Promise(async (resolve, reject) => {
    const timeout = setTimeout(() => {
      isConnecting = false;
      connectPromise = null;
      reject(new Error("Connection timeout"));
    }, timeoutMs);

    try {
      await new Promise((res, rej) => {
        const connectCall = tinode.connect();

        if (connectCall && typeof connectCall.then === "function") {
          connectCall
            .then(() => res())
            .catch((err) => {
              if (tinode.isConnected()) {
                res();
              } else {
                rej(err);
              }
            });
        } else {
          const check = setInterval(() => {
            if (tinode.isConnected()) {
              clearInterval(check);
              res();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(check);
            if (tinode.isConnected()) res();
            else rej(new Error("Connection timeout (polling)"));
          }, timeoutMs - 500);
        }
      });

      await new Promise((r) => setTimeout(r, 300));

      clearTimeout(timeout);
      isConnecting = false;
      connectPromise = null;
      resolve(tinode);
    } catch (err) {
      clearTimeout(timeout);
      isConnecting = false;
      connectPromise = null;

      if (tinode.isConnected()) {
        resolve(tinode);
      } else {
        reject(err);
      }
    }
  });

  return connectPromise;
};

export const getCurrentTinodeInstance = () => tinodeInstance;

export const disconnectTinode = () => {
  if (tinodeInstance) {
    try {
      tinodeInstance.disconnect();
    } catch (err) {
      console.warn("[Tinode] Error disconnecting:", err);
    }
    tinodeInstance = null;
  }
  isCreatingInstance = false;
  instancePromise = null;
  isConnecting = false;
  connectPromise = null;
};
